// Homepage copy, approved by the founder 2026-09-25 (tasks/copy-draft.md).
// Section components read from here; home.test.ts enforces the outward copy rules.
// Headlines are two lines: `line1` in the serif, `line2` set italic in blue.

export type Headline = { line1: string; line2: string };

// A blog post card. `image` is the picture made for that post (public/blog/); without one the card draws a cover
// from `eyebrow` and `title`.
export type BlogEntry = { title: string; href: string; eyebrow: string; summary: string; image?: string };

export const home = {
  meta: {
    title: "Intakall | AI notetaker that fixes meetings",
    description:
      "One person records. Everyone on the team sees the ticket, pull request or thread being discussed, live in the meeting. Then the notes write themselves.",
  },

  nav: {
    links: [
      { label: "How it works", href: "#how-it-works" },
      { label: "Pricing", href: "#pricing" },
      { label: "Field notes", href: "#journal" },
      { label: "FAQ", href: "#faq" },
    ],
    talk: "Talk to us",
    install: "Add to Chrome",
  },

  hero: {
    label: "LIVE IN YOUR NEXT MEETING",
    headline: { line1: "AI notetaker bringing the missing knowledge into your meeting,", line2: "not just transcribing it." } as Headline,
    // Founder-approved wording, 7 Oct 2026.
    sub: "Most AI notetakers stop at the transcript. Intakall puts the ticket, pull request, thread or code you mention on everyone's screen, live. Nothing to remember, nothing to dig for, and your detailed notes are ready when you hang up.",
    cta: "Add to Chrome, it's free",
    secondary: "See how it works",
    micro: "Free plan. No card needed.",
    // Hero demo: a call with the real Intakall sidebar (0.1.5 look). Founder, 2026-09-26.
    // Only shows what the product does: live cards, newest on top, voice ticket drafts.
    meeting: {
      title: "Weekly sync",
      lines: [
        { speaker: "Maya", text: "Acme's asking about the checkout bug again. Didn't we fix that?" },
        { speaker: "Jon", text: "Not yet. My fix is in review, it ships Thursday." },
        { speaker: "Maya", text: "Intakall, raise a ticket to tell Acme when it's live." },
      ],
      slack: {
        title: "Acme: checkout still failing on retry",
        subtitle: "#support · Dana",
      },
      linear: {
        title: "Checkout times out on retry",
        subtitle: "OPS-214 · Jon",
        chips: [
          { text: "in progress", tone: "progress" },
          { text: "payments", tone: "neutral" },
        ],
      },
      pr: {
        title: "Retry payment calls with backoff",
        subtitle: "#482 · acme/checkout",
        chips: [{ text: "review", tone: "review" }],
      },
      draft: {
        title: "Tell Acme when the checkout fix ships",
        summary: "Update Acme once the retry fix for checkout is live.",
        created: "OPS-215",
      },
      sidebar: {
        listening: "Listening for topics...",
        hint: 'Say "Intakall, raise a ticket about ..." to capture an action item',
        placeholder: "Type a ticket request\u2026",
        liveCards: "Turn live cards off",
      },
      tiles: [
        { name: "Maya", initial: "M", video: "" },
        { name: "Jon", initial: "J", video: "" },
      ],
      // After the call: what the team gets in the dashboard (summary, owned action items, transcript).
      post: {
        when: "Today",
        people: "4",
        status: "Notes ready",
        sections: [
          {
            heading: "Checkout failures",
            bullets: ["Acme still seeing checkout fail on retry", "Fix is in review (#482), ships Thursday"],
          },
          { heading: "Customer update", bullets: ["OPS-215 created to tell Acme when it's live"] },
        ],
        actions: [
          { owner: "Jon", task: "Ship the checkout fix Thursday" },
          { owner: "Maya", task: "Tell Acme once it's live" },
        ],
        stamps: ["14:02", "14:03", "14:04"],
      },
    },
  },

  // Every number here must be backed by its source. Swap in Intakall's own customer
  // numbers (with their source line) once ~10 teams have a month or two of usage.
  stats: {
    label: "WHY IT MATTERS",
    headline: { line1: "Meetings run on missing context.", line2: "Yours don’t have to." } as Headline,
    problem: [
      {
        value: "62%",
        label: "of people say they spend too much time searching for information",
        source: "Microsoft Work Trend Index, 2023",
        href: "https://www.microsoft.com/en-us/worklab/work-trend-index/will-ai-fix-work",
      },
      {
        value: "55%",
        label: "say next steps are unclear when a meeting ends",
        source: "Microsoft Work Trend Index, 2023",
        href: "https://www.microsoft.com/en-us/worklab/work-trend-index/will-ai-fix-work",
      },
      {
        value: "1,200",
        label: "app switches a day, costing almost 4 hours a week",
        source: "Harvard Business Review, 2022",
        href: "https://hbr.org/2022/08/how-much-time-and-energy-do-we-waste-toggling-between-applications",
      },
    ],
    facts: [
      { value: "0", label: "bots in your call. Intakall runs in your browser." },
      { value: "1", label: "person records. Everyone on the team sees the cards." },
      { value: "4", label: "tools in the room: GitHub, Linear, Jira and Slack." },
    ],
    hint: "click for the next one",
  },

  howItWorks: {
    label: "HOW IT WORKS",
    headline: { line1: "How Intakall", line2: "actually works" } as Headline,
    steps: [
      { title: "Add to Chrome.", body: "Sign in with Google. Takes a minute." },
      {
        title: "Connect your tools once.",
        body: "GitHub, Linear, Jira or Slack. Your whole team shares them.",
      },
      {
        title: "One person hits record.",
        body: "Nothing records until someone clicks. Everyone else just joins.",
      },
      {
        title: "The work shows up as you talk.",
        body: "Mention a ticket, a PR or a thread and it appears in the sidebar for everyone on your team in the call.",
      },
      {
        title: "The notes are done when you hang up.",
        body: "Transcript, summary and action items, ready for the whole team.",
      },
    ],
    note: "one recorder, whole team covered",
    // Step 5 tablet: the dashboard's meeting notes, wording from the hero film (founder-approved R12, 1 Oct).
    notes: {
      title: "Weekly sync",
      when: "Sep 28, 2026 · 24m",
      summary: [
        "Payments review ahead of Thursday's release.",
        "Decision: the customer hears directly the moment the fix is live, tracked in OPS-215, which Intakall raised during the call. The team also agreed retries need better visibility, so Jon will add retry alerts to the payments dashboard next sprint.",
      ],
      actionItems: [
        { task: "Ship the checkout retry fix (#482)", owner: "Jon", due: "Thursday" },
        { task: "Update the customer once the fix is live", owner: "Maya", due: "Thursday" },
        { task: "Add retry alerts to the payments dashboard", owner: "Jon", due: "Next sprint" },
      ],
    },
  },

  features: {
    label: "WHAT YOU GET",
    headline: { line1: "Less searching in the meeting.", line2: "More getting on with it." } as Headline,
    items: [
      {
        // Founder wording, 7 Oct 2026.
        title: "Code context, live.",
        body: "Mention a function or a file and Intakall shows where it lives and what it does, for everyone in the call. No more searching through repos. (Your code itself never appears on a screen.)",
      },
      {
        // Founder-approved wording, 7 Oct 2026. "The sidebar", not "everyone's": a Slack card reaches only the
        // person who connected Slack until admins can connect it for the team.
        title: "Slack threads, live.",
        body: "Mention a conversation and the Slack thread it came from appears in the sidebar, with who said it and where. No more scrolling back through channels.",
      },
      {
        title: "Pull requests, live.",
        body: "Ask where a fix is up to and the pull request appears in the sidebar, with its review status. No more digging through GitHub mid-call.",
      },
    ],
  },

  integrations: {
    label: "WORKS WITH",
    headline: { line1: "The tools your team", line2: "already lives in." } as Headline,
    body: "Connect once and Intakall knows your team's work. More tools are on the way.",
    tools: [
      { name: "GitHub", logo: "/logos/github.svg" },
      { name: "Linear", logo: "/logos/linear.svg" },
      { name: "Jira", logo: "/logos/jira.svg" },
      { name: "Slack", logo: "/logos/slack.svg" },
    ],
    request: { placeholder: "Which tool should we add next?", button: "Request it" },
  },

  trust: {
    label: "YOUR MEETINGS, YOUR DATA",
    headline: { line1: "Built for meetings", line2: "you’d rather keep private." } as Headline,
    // Founder-approved promises, 7 Oct 2026. Code security goes on the /security page, not here.
    points: [
      {
        title: "Your team only.",
        body: "Only your team sees the notes. Anyone else in the call gets nothing, and the person recording decides who on your team sees them.",
      },
      {
        title: "Encrypted with your team’s own key.",
        body: "Transcripts and summaries are encrypted before they’re stored, with a key that belongs to your team alone.",
      },
      { title: "Your data stays in the EU.", body: "Stored in Ireland, by a UK company under UK-GDPR." },
      {
        title: "Never used to train AI.",
        body: "Your meetings don’t train AI models. On paid plans, that includes our speech-to-text provider.",
      },
      {
        title: "Zero-retention mode.",
        tag: "Enterprise",
        body: "When enabled, no meeting data is retained after the call ends: no transcripts, summaries or recordings.",
      },
    ],
    links: [
      { label: "How we keep it secure", href: "/security" },
      { label: "Who processes your data", href: "/subprocessors" },
    ],
  },

  pricing: {
    label: "PRICING",
    headline: { line1: "Start free.", line2: "Pay when your team does." } as Headline,
    // Prices must match what Stripe charges (Pro $24/mo, $216/yr since 6 Oct).
    tiers: [
      {
        name: "Free",
        tagline: "Get a feel for live meeting intelligence.",
        monthly: 0,
        yearly: 0,
        unit: "forever",
        cta: "Sign up",
        features: ["7 meetings per month", "60-day retention", "GitHub, Linear, Jira and Slack", "Single user"],
      },
      {
        name: "Pro",
        tagline: "For people who live in back-to-back meetings.",
        monthly: 24,
        yearly: 18,
        unit: "per month",
        cta: "Sign up",
        highlighted: true,
        features: ["Unlimited meetings", "365-day retention", "Every integration", "Priority support"],
      },
      {
        name: "Enterprise",
        tagline: "For professionals who need the right context in the call.",
        monthly: 39,
        yearly: 32,
        unit: "per seat / month",
        pricePrefix: "From",
        cta: "Contact sales",
        features: [
          "Everything in Pro",
          "Multi-seat with shared workspace",
          "Live-only mode (nothing stored)",
          "SSO (SAML, Google Workspace)",
          "Admin controls + audit log",
          "Dedicated success manager",
        ],
      },
    ],
  },

  journal: {
    label: "BLOG",
    headline: { line1: "Latest", line2: "from the blog" } as Headline,
    // Summaries are each page's own published meta description (content/*.md).
    entries: [
      {
        title: "What is meeting intelligence?",
        href: "/what-is-meeting-intelligence",
        image: "/blog/what-is-meeting-intelligence.jpg",
        eyebrow: "Meeting intelligence",
        summary:
          "Meeting intelligence turns a live meeting into the context and actions your team needs, not just a transcript after the fact. Here is what it means and how it works.",
      },
      {
        title: "Run your first meeting",
        href: "/guide",
        image: "/blog/run-your-first-meeting.jpg",
        eyebrow: "Getting started",
        summary:
          "A short guide that takes a new host from installing Intakall to a recorded meeting with live context, plus how your team reads the transcript and summary afterward.",
      },
    ] as BlogEntry[],
  },

  faq: [
    {
      q: "What is Intakall?",
      a: "A sidebar for your team's meetings. While you talk, it shows the tickets, pull requests and threads you mention, for everyone on the team in the call. After the meeting you get the transcript, summary and action items.",
    },
    {
      q: "Which meetings does it work in?",
      a: "Meetings in Chrome today. Zoom and Microsoft Teams are on the roadmap.",
    },
    {
      q: "Does everyone need to record?",
      a: "No. One person records, and everyone on the same team in the call sees the cards.",
    },
    {
      q: "How is my meeting data stored?",
      a: "In the EU, encrypted in transit and at rest, with transcripts encrypted per team. Intakall is a UK company and UK-GDPR compliant.",
    },
    {
      q: "How long do you keep my meetings?",
      a: "Free plan: 60-day retention. Pro and Enterprise: 365-day retention. Enterprise can also choose live-only mode (cards during the call, nothing stored). After that, transcripts and everything made from them are deleted automatically. You can also delete a meeting on demand from the dashboard.",
    },
    {
      q: "Do you train AI models on my meetings?",
      a: "We never use your transcripts or anything made from them to train our models. On paid plans our speech-to-text provider is opted out of training too. On the Free plan, it may use audio to improve its own models.",
    },
    {
      q: "Which integrations are supported?",
      a: "GitHub, Linear, Jira and Slack. Tell us which tool you want next.",
    },
    {
      q: "Can I cancel any time?",
      a: "Yes. Cancel from billing settings whenever you like. No calls, no email loops. You keep access until the end of the period you've already paid for.",
    },
    {
      q: "Do you offer an Enterprise plan?",
      a: "Yes. The Enterprise tier ships with multi-seat workspaces, live-only mode, SSO (SAML / Google Workspace), admin controls, audit log, and a dedicated success manager. Talk to us for procurement, DPA or custom retention.",
    },
  ],

  finalCta: {
    // Founder wording, 7 Oct 2026 (Fathom-style close).
    headline: { line1: "Every meeting, already prepared.", line2: "Stop searching. Start deciding." } as Headline,
    body: "Start today, for free.",
    cta: "Add to Chrome, it's free",
    talk: "Talk to us",
  },

  footer: { copyright: "© 2026 Intakall Ltd. All rights reserved." },
} as const;
